import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import applicationService, { Application } from "../services/api/applicationService";

const ApplicationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [application, setApplication] = useState<Application | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/signin?redirect=/applications/" + id);
      return;
    }
    loadApplication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, id]);

  const loadApplication = async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const data = await applicationService.getApplicationById(id);
      setApplication(data);
    } catch (err: any) {
      setError(err.message || "Failed to load application details");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      DRAFT: "bg-gray-100 text-gray-800 border-gray-300",
      PENDING_REVIEW: "bg-orange-100 text-orange-800 border-orange-300",
      APPROVED: "bg-green-100 text-green-800 border-green-300",
      SUBMITTED: "bg-blue-100 text-blue-800 border-blue-300",
      UNDER_REVIEW: "bg-yellow-100 text-yellow-800 border-yellow-300",
      ACCEPTED: "bg-green-100 text-green-800 border-green-300",
      REJECTED: "bg-red-100 text-red-800 border-red-300",
      WAITLISTED: "bg-purple-100 text-purple-800 border-purple-300",
      WITHDRAWN: "bg-gray-100 text-gray-800 border-gray-300",
    };
    return colors[status] || "bg-gray-100 text-gray-800 border-gray-300";
  };

  const getPaymentStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      PENDING: "bg-yellow-100 text-yellow-800",
      PROCESSING: "bg-blue-100 text-blue-800",
      COMPLETED: "bg-green-100 text-green-800",
      FAILED: "bg-red-100 text-red-800",
      REFUNDED: "bg-gray-100 text-gray-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handlePayNow = async () => {
    if (!id) return;

    setPaymentLoading(true);
    setError(null);

    try {
      const response = await applicationService.createPayFastPayment(id);

      if ((response as any).alreadyPaid) {
        window.location.href = `/payment/completion?applicationId=${id}`;
        return;
      }

      // Auto-submit form to PayFast
      const form = document.createElement("form");
      form.method = "POST";
      form.action = response.transactionUrl;

      if (response.paymentData) {
        Object.entries(response.paymentData).forEach(([key, value]) => {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = key;
          input.value = String(value);
          form.appendChild(input);
        });
      }

      document.body.appendChild(form);
      form.submit();
    } catch (err: any) {
      setError(err.message || "Failed to initiate payment");
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg">
            <h3 className="font-semibold mb-2">Error</h3>
            <p>{error || "Application not found"}</p>
            <Link
              to="/my-applications"
              className="inline-block mt-4 text-cyan-600 hover:text-cyan-700 font-medium"
            >
              ← Back to My Applications
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/my-applications"
            className="inline-flex items-center text-cyan-600 hover:text-cyan-700 font-medium mb-4"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to My Applications
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Application Details</h1>
              <p className="text-gray-600 mt-1">
                Application #{application.applicationNumber}
              </p>
            </div>
            <span
              className={`px-4 py-2 rounded-full text-sm font-medium border ${getStatusColor(
                application.status
              )}`}
            >
              {application.status.replace("_", " ")}
            </span>
          </div>
        </div>

        {/* Status Banner: Pending Review */}
        {application.status === "PENDING_REVIEW" && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 mb-6">
            <div className="flex items-start">
              <span className="text-3xl mr-4">⏳</span>
              <div>
                <h3 className="text-lg font-semibold text-orange-900">Application Under Review</h3>
                <p className="text-orange-700 mt-1">
                  Your application has been submitted and is being reviewed by our team.
                  You will receive an email once your application is approved to proceed with payment.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Status Banner: Approved - Pay Now */}
        {application.status === "APPROVED" && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start">
                <span className="text-3xl mr-4">✅</span>
                <div>
                  <h3 className="text-lg font-semibold text-green-900">Application Approved!</h3>
                  <p className="text-green-700 mt-1">
                    Your application has been approved. Please complete the payment to finalize your application.
                  </p>
                </div>
              </div>
              <button
                onClick={handlePayNow}
                disabled={paymentLoading}
                className="flex-shrink-0 ml-4 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors font-semibold text-lg shadow-md"
              >
                {paymentLoading ? "Processing..." : "Pay Now"}
              </button>
            </div>
          </div>
        )}

        {/* Course Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Course Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm text-gray-600">Course</label>
              <p className="text-base font-medium text-gray-900">{application.course.title}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Level</label>
              <p className="text-base font-medium text-gray-900">{application.course.level}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">University</label>
              <p className="text-base font-medium text-gray-900">{application.course.university.name}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Location</label>
              <p className="text-base font-medium text-gray-900">
                {application.course.university.city}, {application.course.university.country}
              </p>
            </div>
            {application.intake && (
              <>
                <div>
                  <label className="text-sm text-gray-600">Intake</label>
                  <p className="text-base font-medium text-gray-900">{application.intake.name}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Application Deadline</label>
                  <p className="text-base font-medium text-gray-900">
                    {formatDate(application.intake.applicationDeadline)}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Personal Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Personal Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm text-gray-600">Given Names</label>
              <p className="text-base font-medium text-gray-900">{application.givenNames || "N/A"}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Family Name</label>
              <p className="text-base font-medium text-gray-900">{application.familyName || "N/A"}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Gender</label>
              <p className="text-base font-medium text-gray-900">
                {application.gender?.replace("_", " ") || "N/A"}
              </p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Date of Birth</label>
              <p className="text-base font-medium text-gray-900">{formatDate(application.dateOfBirth)}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Nationality</label>
              <p className="text-base font-medium text-gray-900">{application.nationality || "N/A"}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Place of Birth</label>
              <p className="text-base font-medium text-gray-900">{application.placeOfBirth || "N/A"}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Email</label>
              <p className="text-base font-medium text-gray-900">{application.email || "N/A"}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">WhatsApp Number</label>
              <p className="text-base font-medium text-gray-900">{application.whatsappNumber || "N/A"}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Passport Number</label>
              <p className="text-base font-medium text-gray-900">{application.passportNumber || "N/A"}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Passport Expiry</label>
              <p className="text-base font-medium text-gray-900">{formatDate(application.passportExpiry)}</p>
            </div>
          </div>

          {application.photoUrl && (
            <div className="mt-6">
              <label className="text-sm text-gray-600 block mb-2">Photo</label>
              <img
                src={application.photoUrl}
                alt="Applicant"
                className="w-32 h-40 object-cover rounded-lg border border-gray-300"
              />
            </div>
          )}
        </div>

        {/* Additional Information */}
        {(application.personalStatement || application.academicHistory || application.workExperience || application.references) && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Additional Information</h2>

            {application.personalStatement && (
              <div className="mb-6">
                <label className="text-sm text-gray-600 block mb-2">Personal Statement</label>
                <p className="text-base text-gray-900 whitespace-pre-wrap">{application.personalStatement}</p>
              </div>
            )}

            {application.academicHistory && (
              <div className="mb-6">
                <label className="text-sm text-gray-600 block mb-2">Study Plan</label>
                <p className="text-base text-gray-900 whitespace-pre-wrap">{application.academicHistory}</p>
              </div>
            )}

            {application.workExperience && (
              <div className="mb-6">
                <label className="text-sm text-gray-600 block mb-2">Work Experience</label>
                <p className="text-base text-gray-900 whitespace-pre-wrap">{application.workExperience}</p>
              </div>
            )}

            {application.references && (
              <div>
                <label className="text-sm text-gray-600 block mb-2">References</label>
                <p className="text-base text-gray-900 whitespace-pre-wrap">{application.references}</p>
              </div>
            )}
          </div>
        )}

        {/* Documents */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Documents ({application.documents.length})
          </h2>
          {application.documents.length === 0 ? (
            <p className="text-gray-600">No documents uploaded yet.</p>
          ) : (
            <div className="space-y-3">
              {application.documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="flex-shrink-0">
                      <svg className="w-10 h-10 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{doc.name}</p>
                      <p className="text-sm text-gray-500">
                        {doc.documentType.replace(/_/g, " ")} • {(doc.size / 1024).toFixed(2)} KB
                      </p>
                      <p className="text-xs text-gray-400">
                        Uploaded: {formatDate(doc.uploadedAt)}
                      </p>
                    </div>
                    <div>
                      {doc.isVerified ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Pending
                        </span>
                      )}
                    </div>
                  </div>
                  {doc.url && (
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-4 px-4 py-2 text-sm font-medium text-cyan-600 hover:text-cyan-700 border border-cyan-600 rounded-lg hover:bg-cyan-50"
                    >
                      View
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Payment Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm text-gray-600">Payment Status</label>
              <span
                className={`inline-block mt-1 px-3 py-1 rounded-full text-sm font-medium ${getPaymentStatusColor(
                  application.paymentStatus
                )}`}
              >
                {application.paymentStatus}
              </span>
            </div>
            {application.paymentReference && (
              <div>
                <label className="text-sm text-gray-600">Transaction Reference</label>
                <p className="text-base font-medium text-gray-900">{application.paymentReference}</p>
              </div>
            )}
            {application.paidAt && (
              <div>
                <label className="text-sm text-gray-600">Payment Date</label>
                <p className="text-base font-medium text-gray-900">{formatDate(application.paidAt)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Application Timeline</h2>
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-cyan-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-900">Application Created</p>
                <p className="text-sm text-gray-500">{formatDate(application.createdAt)}</p>
              </div>
            </div>

            {application.paidAt && (
              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-900">Payment Completed</p>
                  <p className="text-sm text-gray-500">{formatDate(application.paidAt)}</p>
                </div>
              </div>
            )}

            {application.submittedAt && (
              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-900">Application Submitted</p>
                  <p className="text-sm text-gray-500">{formatDate(application.submittedAt)}</p>
                </div>
              </div>
            )}

            <div className="flex items-start">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-900">Last Updated</p>
                <p className="text-sm text-gray-500">{formatDate(application.updatedAt)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        {application.status === "DRAFT" && (
          <div className="mt-6 flex justify-end space-x-4">
            <Link
              to={`/apply/${application.course.id}?applicationId=${application.id}`}
              className="px-6 py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-medium"
            >
              Continue Application
            </Link>
          </div>
        )}

        {application.status === "APPROVED" && (
          <div className="mt-6 flex justify-end space-x-4">
            <button
              onClick={handlePayNow}
              disabled={paymentLoading}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors font-semibold shadow-md"
            >
              {paymentLoading ? "Processing..." : "Pay Now & Complete Application"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationDetails;
